import { todayStr, toLocalDateStr } from "../../lib/date";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Truck } from "lucide-react";
import { Button } from "../ui/button";
import { getPortalTransportWeeklySheet } from "../../lib/api";

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Color coding mirrors the staff Weekly Sheet / source transportation spreadsheet.
const KIND_STYLE = {
  full: { background: "#B7E1CD", color: "#0d3d24" },
  half: { background: "#D9EAD3", color: "#274d2c" },
  alt: { background: "#FFF2CC", color: "#7a5b00" },
  late: { background: "#FFF2CC", color: "#7a5b00" },
  early: { background: "#FFF2CC", color: "#7a5b00" },
  partial: { background: "#FFF2CC", color: "#7a5b00" },
  absent: { background: "#F4CCCC", color: "#7a1f1f" },
  pause: { background: "#E7E0F5", color: "#4b3b7a" },
  off: { background: "#FFFFFF", color: "#9ca3af" },
  no_school: { background: "#F1F3F5", color: "#94a3b8", fontStyle: "italic" },
};
const OVERRIDE_STYLE = { background: "#FCE5CD", color: "#7a3e00" };

function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return toLocalDateStr(d);
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return toLocalDateStr(d);
}
function shortDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function PortalWeeklySheet() {
  const [weekStart, setWeekStart] = useState(mondayOf(todayStr()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (ws) => {
    setLoading(true);
    try {
      const res = await getPortalTransportWeeklySheet(ws);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load the weekly overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(weekStart); }, [weekStart, load]);

  const cellStyle = (cell) => (cell.override ? OVERRIDE_STYLE : (KIND_STYLE[cell.default_kind] || KIND_STYLE.off));

  const weekLabel = data
    ? `${shortDate(data.dates[0])} – ${shortDate(data.dates[4])}, ${new Date(data.dates[0] + "T00:00:00").getFullYear()}`
    : "";

  return (
    <div data-testid="portal-weekly-sheet">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-[#00305E]" />
          <div>
            <h2 className="text-xl font-bold text-[#00305E] leading-tight">Weekly Overview</h2>
            <p className="text-sm text-slate-500">Weekly Transportation &amp; Attendance — your assigned students (read-only)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="portal-weekly-prev"
            onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[170px] text-center font-semibold text-slate-700" data-testid="portal-weekly-label">{weekLabel}</span>
          <Button variant="outline" size="icon" data-testid="portal-weekly-next"
            onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="ml-1"
            onClick={() => setWeekStart(mondayOf(todayStr()))}
            data-testid="portal-weekly-this-week">This week</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-3 text-xs text-slate-600">
        <Legend swatch="#B7E1CD" label="Full day" />
        <Legend swatch="#D9EAD3" label="Half day" />
        <Legend swatch="#FFF2CC" label="Late in / early out / alt plan" />
        <Legend swatch="#F4CCCC" label="Absent" />
        <Legend swatch="#E7E0F5" label="On pause" />
        <Legend swatch="#FCE5CD" label="Staff note" />
      </div>

      {loading ? (
        <div className="text-slate-500 py-10 text-center">Loading…</div>
      ) : !data || data.rows.length === 0 ? (
        <div className="text-slate-500 py-10 text-center border rounded-lg" data-testid="portal-weekly-empty">
          No students are currently assigned to your transportation service.
        </div>
      ) : (
        <div className="overflow-auto border rounded-lg max-h-[calc(100vh-260px)]">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#00305E] text-white">
                <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-[#00305E] z-20 min-w-[160px]">Student</th>
                <th className="px-3 py-2 text-left font-semibold min-w-[180px]">Transportation Contact</th>
                <th className="px-3 py-2 text-center font-semibold min-w-[90px]">Pick-up</th>
                {data.dates.map((d, i) => {
                  const m = data.day_meta[d];
                  return (
                    <th key={d} className="px-2 py-2 text-center font-semibold min-w-[120px]">
                      <div>{DAY_LABELS[i]}</div>
                      <div className="text-[11px] font-normal opacity-80">{shortDate(d)}</div>
                      {!m.is_school_day ? (
                        <div className="text-[10px] font-normal text-amber-200">No school</div>
                      ) : m.is_half_day ? (
                        <div className="text-[10px] font-normal text-emerald-200">Half day</div>
                      ) : null}
                    </th>
                  );
                })}
                <th className="px-3 py-2 text-left font-semibold min-w-[200px]">Transportation Address</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, ri) => (
                <tr key={row.student_id} className={ri % 2 ? "bg-slate-50" : "bg-white"}>
                  <td className="px-3 py-2 font-semibold text-slate-800 sticky left-0 z-10 whitespace-nowrap"
                    style={{ background: ri % 2 ? "#f8fafc" : "#fff" }} data-testid={`portal-weekly-row-${row.student_id}`}>
                    {row.name}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-pre-line">{row.transportation_contact || "—"}</td>
                  <td className="px-3 py-2 text-center text-xs text-slate-600">{row.pickup_time || "—"}</td>
                  {data.dates.map((d) => {
                    const cell = row.cells[d];
                    return (
                      <td key={d} className="p-0 border-l border-slate-200 align-top">
                        <div
                          data-testid={`portal-weekly-cell-${row.student_id}-${d}`}
                          className="min-h-[42px] px-2 py-1 text-xs whitespace-pre-line flex items-center justify-center text-center"
                          style={cellStyle(cell)}
                        >
                          {cell.value || ""}
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-xs text-slate-600 border-l border-slate-200 whitespace-pre-line">{row.home_address || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Legend = ({ swatch, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="inline-block w-3 h-3 rounded-sm border border-slate-300" style={{ background: swatch }} />
    {label}
  </span>
);
